import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Text,
    Toolbar,
    ToolbarButton,
    Dropdown,
    Option,
    Field,
    Radio, RadioGroup,
    Button
} from '@fluentui/react-components';
import { DocumentData20Regular, ArrowLeft20Regular, ArrowDownload20Regular } from '@fluentui/react-icons';
import { useAppStyles } from '../../styles/appStyles';
import { useReportsPageStyles } from '../../styles/reportsPageStyles';
import EupsSelector from '../EupsSelector';
import ProgressPage from "../ProgressPage";
import { useKeoAnnualReportWithPermitData } from "../../hooks/useKeoAnnualReportWithPermitData";
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { config } from "../../../config/environment";
import { EupType } from "../../../model";
import { useDevice } from '../../../model/DeviceContext';

const appInsights = new ApplicationInsights({
    config: { instrumentationKey: config.appInsights.instrumentationKey }
});
appInsights.loadAppInsights();

interface AnnualReportWithPermitProps {
    onBack: () => void;
    companyId: string | null;
}

const AnnualReportWithPermit: React.FC<AnnualReportWithPermitProps> = ({ onBack, companyId }) => {
    const { t } = useTranslation();
    const styles = useAppStyles();
    const reportStyles = useReportsPageStyles();
    const { subscription, isTrial } = useDevice();
    const ReportId = "CW12";

    // Stany formularza
    const [selectedEupId, setSelectedEupId] = useState<string | null>(null);
    const [selectedRadio, setSelectedRadio] = useState<string>("");
    const [dateError, setDateError] = useState<string | null>(null);

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2019 + 1 }, (_, i) => (2019 + i).toString());
    const [selectedYear, setSelectedYear] = useState<string>("");

    // Handler dla zmiany EUP - automatycznie ustawia selected
    const handleEupChange = (eupId: string | null) => {
        setSelectedEupId(eupId);
    };

    // Hook dla KEO annual report with permit data
    const {
        showProgress,
        fetchDataFunction,
        fetchKeoData,
        handleProgressComplete,
        backTo
    } = useKeoAnnualReportWithPermitData();

    // Funkcja obsługująca generowanie raportu
    const handleGenerateKeoReport = () => {
        if (!selectedEupId || !selectedRadio) {
            setDateError("Wybierz punkt odbioru i zakres czasowy");
            return;
        }
        // Znajdź dane firmy i EUP
        const company = subscription?.companies?.find(c => c.companyId === companyId);
        const eup = company?.eups?.find(e => e.eupId === selectedEupId);

        if (!company || !eup) {
            console.error("Nie znaleziono danych firmy lub EUP");
            return;
        }

        // Utworzenie eupType na podstawie danych z subscription
        const eupType: EupType = {
            bdoentityname: company.name, // Dodane pole
            eupId: eup.eupId,
            identificationNumber: eup.identificationNumber, 
            name: eup.name,
            clientId: company.clientId,
            clientSecret: company.clientSecret,
            isTrial: isTrial
        };

        let year = new Date().getFullYear();
        let sheetName: string;

        // Ustal daty na podstawie wybranej opcji radio
        if (selectedRadio === "A") {
            // Bieżący rok
            const now = new Date();
            year = now.getFullYear();

            sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} ${year}`;

        } else if (selectedRadio === "B") {
            // Poprzedni rok
            const now = new Date();
            year = now.getFullYear() - 1;

            sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} ${year}`;
        } else if (selectedRadio === "C") {
            if (!selectedYear) {
                setDateError(t("form_error_select_year_for_option_c"));
                return;
            }
            year = parseInt(selectedYear, 10);
            if (isNaN(year)) {
                setDateError(t("report_error_invalid_year"));
                return;
            }
            sheetName = `${eupType.identificationNumber.replace(/\//g, "|")} ${ReportId} ${year}`;
        } else {
            console.error("Nieprawidłowe parametry dla generowania raportu");
            return;
        }

        // Wywołaj funkcję pobierania danych
        fetchKeoData(
            eupType,
            year,
            sheetName
        );
    };
    // Sprawdź czy można włączyć przycisk generowania
    const canGenerate = selectedEupId && selectedRadio &&
        (selectedRadio !== "C" || (selectedYear && !dateError));
    // Jeśli pokazujemy progress, renderuj ProgressPage
    if (showProgress && fetchDataFunction) {
        // Pobierz nazwę EUP dla wyświetlenia
        const company = subscription?.companies?.find(c => c.companyId === companyId);
        const eup = company?.eups?.find(e => e.eupId === selectedEupId);
        const eupDisplayName = eup ? `${eup.identificationNumber} - ${eup.name}` : undefined;

        return (
            <ProgressPage
                fetchData={fetchDataFunction}
                loadingMessage={t("report_loading_generating_report")}
                successMessage={t("report_success_data_fetched")}
                backTo="/"
                autoReturnDelay={3000}
                onComplete={handleProgressComplete}
                reportCode="CW12"
                reportName={t('AccordionHeader_generated_reports_option2', 'Zestawienie roczne z pozwoleniem')}
                eupName={eupDisplayName}
                additionalInfo="Generowanie raportu może potrwać dłuższą chwilę..."
            />
        );
    }

    return (
        <div className={styles.appPageContainer}>
            <div className={styles.appPageHeader}>
                <DocumentData20Regular className={styles.appPageHeaderIcon} />
                <Text className={styles.appPageTitle}>
                    {t('AccordionHeader_generated_reports_option2', 'ZZestawienie roczne')}
                </Text>
            </div>

            <Toolbar className={styles.toolbar}>
                <ToolbarButton
                    className={styles.toolbarButton}
                    aria-label={t('common.back', 'Powrót')}
                    icon={<ArrowLeft20Regular className={styles.backIcon} />}
                    onClick={onBack}
                >
                    {t('common.back', 'Powrót')}
                </ToolbarButton>
            </Toolbar>

            {companyId && (
                <EupsSelector
                    companyId={companyId}
                    onEupChange={handleEupChange}
                    initialSelectedEupId={selectedEupId}
                />
            )}

            <div className={reportStyles.content}>
                <Field label={t("ReportsControlCard_Option1_Radio_Header")}>
                    <RadioGroup
                        value={selectedRadio} // Upewnij się, że wartość jest kontrolowana
                        onChange={(_, data) => {
                            setSelectedRadio(data.value); // Ustaw wartość wybranego radio
                            setDateError(null); // Czyść błędy daty
                        }}
                        disabled={!selectedEupId}

                    >
                        <Radio value="A" label={t("ReportsControlCard_Option2_Radio_A")} />
                        <Radio value="B" label={t("ReportsControlCard_Option2_Radio_B")} />
                        {/* Rok wybrany z listy od 2019 do aktualnego */}

                        <Radio value="C" label={
                            <>
                                {t("ReportsControlCard_Option2_Radio_C")}
                                {/* Dropdownlist od 2019 do aktualnego roku */}

                                <Dropdown
                                    placeholder={t("form_placeholder_select_year")}
                                    value={selectedYear}
                                    onOptionSelect={(_, data) => setSelectedYear(data.optionValue as string)}
                                    disabled={selectedRadio !== "C"}
                                >
                                    {years.map(year => (
                                        <Option key={year} value={year}>
                                            {year}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </>
                        }
                        />
                    </RadioGroup>
                    {dateError && (
                        <Text style={{ color: "red" }}>{dateError}</Text>
                    )}
                </Field>
                <div className={reportStyles.buttonWrapper}>
                    <Button
                        appearance="primary"
                        className={styles.buttonBottom}
                        disabled={!canGenerate}
                        onClick={handleGenerateKeoReport}
                        icon={<ArrowDownload20Regular />}
                    >
                        {t("ReportsControlCard_button_generate")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
export default AnnualReportWithPermit;